import React, { useMemo } from 'react';
import type { NavBarProps } from '../NavBar';
import { NavBar } from '../NavBar';
import type { EventInfoFragmentFragment } from '@graphql/fragments/event.info.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventPageType } from '@graphql/generated-base';
import { Category } from '@graphql/generated-base';
import { StyledRoot, StyledTitle, styles } from './Header.styles';
import { ThemeSprites } from '@shared/utils/styleApplicator/ThemeSprites';
import { themeCanvasBoxProps } from '@shared/utils/styleApplicator/themeCanvasBoxProps';
import type { ApplicationContext } from '@shared/utils/styleApplicator/styleApplicator.types';
import { BRANNAN_TITLE_BANNER_MEDIA, useTitleBannerCanvas } from '../../LayoutBrannan.theme';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Link } from '@react-router';
import { GuestLanguageSelector } from '@apps/guest/components/GuestLanguageSelector';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core/i18n';
import { isInIframe } from '@shared/utils/isInIframe';
import type { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useColorPaletteProvider } from '../../../ColorPaletteProvider/ColorPaletteProvider';
import { getButtonPropsBasedOnBackground } from '../../../layout.utils';
import { Edit } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { EVENT_DISPLAY_NAME_MAX_LENGTH } from '@shared/constants/textLimits';

const TITLE_BANNER_ART_CONTEXT: ApplicationContext = { component: 'title-banner' };

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

export interface HeaderProps
  extends Readonly<{
    eventHandle: string;
    info: EventInfoFragmentFragment;
    eventDisplayName?: string | null;
    pages: ReadonlyArray<EventPageFragment>;
    navBarVariant: NavBarProps['variant'];
    applicatorProp: object;
    handleMenuClicked?: (item: string, utm?: WritableUtmParameters) => void;
    handleEditEventClicked?: () => void;
    eventProps: NavbarEventPropsInterface;
    page: Maybe<EventPageType>;
    pageSlug?: string;
    children?: React.ReactNode;
  }> {}

const Header: React.FC<HeaderProps> = ({
  applicatorProp,
  eventHandle,
  info,
  eventDisplayName,
  pages,
  navBarVariant,
  handleMenuClicked,
  handleEditEventClicked,
  eventProps,
  page,
  pageSlug,
  children,
  ...restProps
}) => {
  const { isAdmin } = useEventUserRole();
  const [isMobile] = useResponsive({ values: { _: true, sm2: false } });
  const [isXsScreen] = useResponsive({ values: { _: true, xs: false } });
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pageColor, pageFontColor, artBackgroundFontColor } = useColorPaletteProvider();

  const { t2 } = useTranslation('eventMenu');
  const editEventText = t2('editEvent');
  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');
  const customEditEventButtonProps = useMemo(() => (pageColor ? { ...getButtonPropsBasedOnBackground(pageColor), startIcon: <Edit size={16} /> } : {}), [pageColor]);
  const pageFontColorProps = useMemo(() => (pageFontColor ? { color: pageFontColor } : {}), [pageFontColor]);

  const isPreviewing = isInIframe();

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editText',
        inlineEditData: { textContent: eventDisplayName || '', label: inlineEditorTransFn.displayName(), validationConstraints: { maxLength: EVENT_DISPLAY_NAME_MAX_LENGTH } },
      },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  // PRNT-4898 bundled path: small-screen title-banner theme art rendered as React.
  // Empty/inert unless the websiteBundleStyleApplicator flag is affirmatively 'on'.
  const titleBannerCanvas = useTitleBannerCanvas();

  return (
    <>
      <StyledRoot
        {...restProps}
        position={'relative'}
        {...applicatorProp}
        data-uh="nah"
        {...themeCanvasBoxProps(titleBannerCanvas)}
      >
        {/* First children — the DOM position the legacy applicator's insertBefore produced. */}
        {titleBannerCanvas.sprites.length > 0 && (
          <ThemeSprites
            sprites={titleBannerCanvas.sprites}
            context={TITLE_BANNER_ART_CONTEXT}
            media={BRANNAN_TITLE_BANNER_MEDIA}
          />
        )}
        <Flex
          width="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={[291, 200, null, 'initial']}
        >
          <Box
            height={{ _: 'auto', sm2: 'auto' }}
            transition={animationTransition('height')}
            width="100%"
            {...pageFontColorProps}
          >
            <StyledTitle
              __css={styles.title}
              data-testid="header-title"
              {...(isMobile && { color: artBackgroundFontColor })}
            >
              <InlineEditor
                elementLabel={inlineEditorTransFn.displayName()}
                labelPosition="bottom"
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                }}
                inset={isMobile ? -12 : -16}
                componentName="eventDisplayName"
                pageName={page}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  <TextV2 typographyVariant={isXsScreen ? 'display4' : 'display6'}>{eventDisplayName}</TextV2>
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </StyledTitle>
            {/* Header controls, top-right: the guest language picker stacked above the admin
                Edit Event button. Stacking keeps the pair narrow so it doesn't run into the
                event title — the picker renders for guests, Edit Event only for admins on desktop. */}
            <Flex
              position="absolute"
              top="19px"
              right="16px"
              flexDirection="column"
              alignItems="flex-end"
              gap={3}
            >
              <GuestLanguageSelector surface="header" />
              {isAdmin && !isPreviewing && navBarVariant === 'inline' && (
                <ButtonV2
                  onClick={handleEditEventClicked}
                  intent="primary"
                  size="sm"
                  typographyVariant="button1"
                  variant="outline"
                  as={Link}
                  to={`/${eventHandle}/edit/dashboard`}
                  {...customEditEventButtonProps}
                >
                  {editEventText}
                </ButtonV2>
              )}
            </Flex>
            <NavBar
              handleMenuClicked={handleMenuClicked}
              title={eventDisplayName}
              variant={navBarVariant}
              data-testid={'header-nav'}
              eventHandle={eventHandle}
              pages={pages}
              eventProps={eventProps}
            />
            {!isMobileOrTablet && children}
          </Box>
        </Flex>
      </StyledRoot>

      {/* children will typically be the header photo, which should be placed below the component title banner on mobile/tablet*/}
      {isMobileOrTablet && children}
    </>
  );
};

Header.displayName = 'Header';

export { Header };
